import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Menu } from 'antd';
import { EllipsisOutlined, SearchOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import {
  REMOTE_CONFIG_KEYS,
  getProjectAccordingToDomain,
  resolveOrgAccess,
  useIsSelfModeTimeTracker,
  useRemoteConfigAccess,
} from 'packages/utils';
import { get, set } from 'packages/utils/sessionStorage';

import { buildSidebarMenuItems } from './buildSidebarMenuItems';
import { DivisionSelector } from './components/DivisionSelector';
import OrganizationSelector from './components/OrganizationSelector';
import { SidebarBottom } from './components/SidebarBottom';
import SidebarSearch from './components/SidebarSearch';
import { SIDEBAR_OPEN_KEYS_STORAGE_KEY } from './constants';
import styles from './styles.module.less';
import {
  getMenuNavigationMap,
  getRailMenuItems,
  getRequiredOpenKeys,
  getSelectedMenuKey,
  isNestedMenuKey,
  parseStoredOpenKeys,
  persistableOpenKeys,
  resolveOpenKeys,
} from './utils/sidebarNav';

const EMPTY_DIVISIONS = {};

const renderExpandIcon = ({ isOpen }) => (
  <span aria-hidden className={styles.expandIcon}>
    <span className="material-symbols-outlined">
      {isOpen ? 'keyboard_arrow_down' : 'chevron_right'}
    </span>
  </span>
);

const withRowTitles = (items, navigationMap, intl) =>
  items.map(item => {
    const labelId = navigationMap[item.key]?.labelId;
    return {
      ...item,
      title: labelId ? intl.formatMessage({ id: labelId }) : undefined,
      children: item.children ? withRowTitles(item.children, navigationMap, intl) : undefined,
    };
  });

const SideBarMenu = React.memo(({ collapsed = false, isDrawerNav = false, onToggleCollapse }) => {
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { pathname } = useLocation();
  const { id: orgId } = useSelector(({ organizations }) => organizations.organization);
  const claims = useSelector(({ user }) => user.access.data?.claims);
  const orgUserAccess = useSelector(({ user }) => user.orgUser?.data?.access);
  const userOrgAccess = resolveOrgAccess(claims, orgId, orgUserAccess);
  const userOrgDivisions =
    useSelector(({ divisions }) => divisions?.assigned?.data?.divisions) || EMPTY_DIVISIONS;
  const { isAllowed: timeTrackerUser } = useRemoteConfigAccess(REMOTE_CONFIG_KEYS.TIME_TRACKER);
  const { isSupervisor, supervisorThirdParty, isAdmin } = useIsSelfModeTimeTracker();

  const brandValues = getProjectAccordingToDomain();
  const { isAllowed: locationsAlertsOrgsAllowed } = useRemoteConfigAccess(
    REMOTE_CONFIG_KEYS.LOCATION_ALERTS,
  );
  const { isAllowed: emergenciesAllowed } = useRemoteConfigAccess(REMOTE_CONFIG_KEYS.EMERGENCIES);
  const { isAllowed: workerScoreAllowed } = useRemoteConfigAccess(REMOTE_CONFIG_KEYS.WORKER_SCORE);
  const { isAllowed: costCentersAllowed } = useRemoteConfigAccess(REMOTE_CONFIG_KEYS.COST_CENTERS);

  const selectedKey = getSelectedMenuKey(pathname);
  const requiredOpenKeys = useMemo(
    () => getRequiredOpenKeys(selectedKey),
    [selectedKey],
  );
  const selectedKeys = useMemo(
    () => (selectedKey ? [selectedKey] : []),
    [selectedKey],
  );
  const [userOpenKeys, setUserOpenKeys] = useState(() =>
    parseStoredOpenKeys(get(SIDEBAR_OPEN_KEYS_STORAGE_KEY, [])),
  );
  const [closedRouteOpenKeys, setClosedRouteOpenKeys] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchFilterReset, setSearchFilterReset] = useState(0);
  const effectiveRequiredOpenKeys = requiredOpenKeys.filter(
    key => !closedRouteOpenKeys.includes(key),
  );
  const openKeys = resolveOpenKeys(userOpenKeys, effectiveRequiredOpenKeys);

  const openSearch = useCallback(() => {
    setSearchFilterReset(token => token + 1);
    setIsSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  useEffect(() => {
    const handleSearchShortcut = event => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openSearch();
      }
    };

    window.addEventListener('keydown', handleSearchShortcut);
    return () => window.removeEventListener('keydown', handleSearchShortcut);
  }, [openSearch]);

  useEffect(() => {
    setClosedRouteOpenKeys([]);
    setUserOpenKeys(currentKeys => {
      const withoutPeekedNested = currentKeys.filter(key => !isNestedMenuKey(key));
      return withoutPeekedNested.length === currentKeys.length
        ? currentKeys
        : withoutPeekedNested;
    });
  }, [selectedKey]);

  const onOpenChange = useCallback(
    nextOpenKeys => {
      const closedRequiredKeys = requiredOpenKeys.filter(key => !nextOpenKeys.includes(key));
      const effectiveRequiredKeys = requiredOpenKeys.filter(
        key => !closedRequiredKeys.includes(key),
      );
      setClosedRouteOpenKeys(closedRequiredKeys);
      const resolved = resolveOpenKeys(nextOpenKeys, effectiveRequiredKeys);
      const extras = resolved.filter(key => !effectiveRequiredKeys.includes(key));
      setUserOpenKeys(extras);
      set(SIDEBAR_OPEN_KEYS_STORAGE_KEY, persistableOpenKeys(extras, effectiveRequiredKeys));
    },
    [requiredOpenKeys],
  );

  const items = useMemo(
    () =>
      buildSidebarMenuItems({
        url,
        userOrgAccess,
        userOrgDivisions,
        timeTrackerUser,
        isSupervisor,
        supervisorThirdParty,
        isAdmin,
        locationsAlertsOrgsAllowed,
        emergenciesAllowed,
        workerScoreAllowed,
        costCentersAllowed,
      }),
    [
      url,
      userOrgAccess,
      userOrgDivisions,
      timeTrackerUser,
      isSupervisor,
      supervisorThirdParty,
      isAdmin,
      locationsAlertsOrgsAllowed,
      emergenciesAllowed,
      workerScoreAllowed,
      costCentersAllowed,
    ],
  );

  const navigationMap = useMemo(() => getMenuNavigationMap(items), [items]);
  const searchableItems = useMemo(() => {
    const results = [];
    const collect = nodes => {
      nodes.forEach(item => {
        if (!item || typeof item !== 'object') return;
        const route = navigationMap[item.key];
        if (route?.to) {
          results.push({
            key: item.key,
            to: route.to,
            target: route.target,
            label: route.labelId
              ? intl.formatMessage({ id: route.labelId })
              : typeof item.label === 'string'
                ? item.label
                : item.key,
            icon: item.icon,
          });
        }
        if (item.children) collect(item.children);
      });
    };
    collect(items);
    return results;
  }, [intl, items, navigationMap]);

  // Rail clips the label away, so the row carries the destination and the tooltip.
  const menuItems = useMemo(
    () =>
      collapsed
        ? withRowTitles(getRailMenuItems(items, selectedKey), navigationMap, intl)
        : items,
    [collapsed, intl, items, navigationMap, selectedKey],
  );

  const onRailItemClick = useCallback(
    ({ key }) => {
      const { to, target } = navigationMap[key] || {};
      if (!to) return;
      if (target === '_blank') {
        window.open(to, '_blank', 'noopener,noreferrer');
        return;
      }
      history.push(to);
    },
    [history, navigationMap],
  );

  const toggleLabel = intl.formatMessage({
    id: collapsed ? 'sidebar.nav.expand' : 'sidebar.nav.collapse',
  });
  const organizationLabel = intl.formatMessage({ id: 'config.division.organization' });
  const divisionsLabel = intl.formatMessage({ id: 'sidebar.nav.divisions' });
  const brandLogoClassName =
    brandValues.project === 'operonda' ? styles.brandLogo : styles.brandLogoAlt;

  return (
    <div
      className={classnames(
        styles.content,
        isDrawerNav && styles.drawerNav,
        !isDrawerNav && styles.desktopSidebar,
        collapsed && styles.railNav,
      )}
    >
      <div className={styles.fixedTop}>
        <div className={styles.brandRow}>
          <div className={styles.brand}>
            <img alt="" className={brandLogoClassName} src={brandValues.logo} />
            <span className={styles.brandTitle}>{brandValues.title}</span>
          </div>
          <div className={styles.headerActions}>
            {!collapsed || isDrawerNav ? (
              <button
                aria-label={intl.formatMessage({ id: 'general.search' })}
                className={styles.searchToggle}
                onClick={openSearch}
                type="button"
              >
                <span aria-hidden className="material-symbols-outlined">
                  search
                </span>
              </button>
            ) : null}
            {isDrawerNav ? null : (
              <button
                aria-expanded={!collapsed}
                aria-label={toggleLabel}
                className={classnames(
                  styles.collapseToggle,
                  collapsed && styles.collapseToggleOverlay,
                )}
                onClick={onToggleCollapse}
                title={toggleLabel}
                type="button"
              >
                <span aria-hidden className="material-symbols-outlined">
                  dock_to_right
                </span>
              </button>
            )}
          </div>
        </div>
        <div className={styles.selectors}>
          <div className={styles.selectorsFull}>
            <OrganizationSelector desktopTheme={!isDrawerNav} />
            <DivisionSelector desktopTheme={!isDrawerNav} />
          </div>
          <div className={styles.selectorsRail}>
            <button
              aria-label={organizationLabel}
              className={styles.railSelector}
              onClick={onToggleCollapse}
              title={organizationLabel}
              type="button"
            >
              <span aria-hidden className="material-symbols-outlined">
                business
              </span>
            </button>
            <button
              aria-label={divisionsLabel}
              className={styles.railSelector}
              onClick={onToggleCollapse}
              title={divisionsLabel}
              type="button"
            >
              <span aria-hidden className="material-symbols-outlined">
                account_tree
              </span>
            </button>
          </div>
        </div>
      </div>
      <div className={styles.scrollableMenu}>
        <Menu
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          onClick={collapsed ? onRailItemClick : undefined}
          mode="inline"
          inlineIndent={0}
          theme="dark"
          items={menuItems}
          className={styles.sideBarMenu}
          expandIcon={renderExpandIcon}
        />
        {collapsed ? (
          <>
            <button
              aria-label={intl.formatMessage({ id: 'general.search' })}
              className={classnames(styles.railExpandButton, styles.railSearchButton)}
              onClick={openSearch}
              title={intl.formatMessage({ id: 'general.search' })}
              type="button"
            >
              <SearchOutlined aria-hidden />
            </button>
            <button
              aria-label={toggleLabel}
              className={styles.railExpandButton}
              onClick={onToggleCollapse}
              title={toggleLabel}
              type="button"
            >
              <EllipsisOutlined aria-hidden />
            </button>
          </>
        ) : null}
      </div>
      <div className={styles.footerDivider} />
      <div className={styles.fixedBottom}>
        <SidebarBottom collapsed={collapsed} desktopTheme={!isDrawerNav} />
      </div>
      <SidebarSearch
        filterResetToken={searchFilterReset}
        onClose={closeSearch}
        open={isSearchOpen}
        searchableItems={searchableItems}
      />
    </div>
  );
});

export default SideBarMenu;
